import type { Object3D } from 'three';

import type { KeyPoseFkJointId, KeyPoseIkEffectorId } from '../types';

export interface KeyPoseIkEffectorConfig {
  id: KeyPoseIkEffectorId;
  boneNames: readonly string[];
  color: string;
  label: string;
}

export const KEY_POSE_IK_EFFECTOR_IDS = [
  'head',
  'pelvis',
  'left_wrist',
  'right_wrist',
  'left_ankle',
  'right_ankle',
] as const satisfies readonly KeyPoseIkEffectorId[];

export const KEY_POSE_IK_ANKLE_EFFECTOR_IDS = [
  'left_ankle',
  'right_ankle',
] as const satisfies readonly KeyPoseIkEffectorId[];

/** Effectors that follow body motion instead of being world-locked during pelvis IK. */
export const KEY_POSE_IK_PELVIS_MOVE_BODY_FOLLOW_EFFECTOR_IDS = [
  'head',
  'left_wrist',
  'right_wrist',
] as const satisfies readonly KeyPoseIkEffectorId[];

const KEY_POSE_IK_PELVIS_MOVE_BODY_FOLLOW_EFFECTOR_ID_SET =
  new Set<KeyPoseIkEffectorId>(
    KEY_POSE_IK_PELVIS_MOVE_BODY_FOLLOW_EFFECTOR_IDS,
  );

const KEY_POSE_FK_JOINT_IDS_BY_IK_EFFECTOR_ID: Record<
  KeyPoseIkEffectorId,
  readonly KeyPoseFkJointId[]
> = {
  head: ['head', 'neck'],
  pelvis: ['spine3', 'spine2', 'spine1', 'spine0', 'pelvis'],
  left_wrist: ['left_collar', 'left_shoulder', 'left_elbow', 'left_wrist'],
  right_wrist: ['right_collar', 'right_shoulder', 'right_elbow', 'right_wrist'],
  left_ankle: ['left_hip', 'left_knee', 'left_ankle', 'left_foot'],
  right_ankle: ['right_hip', 'right_knee', 'right_ankle', 'right_foot'],
};

const KEY_POSE_IK_EFFECTOR_BY_FK_JOINT_ID: Record<
  KeyPoseFkJointId,
  KeyPoseIkEffectorId
> = {
  head: 'head',
  neck: 'head',
  spine3: 'pelvis',
  spine2: 'pelvis',
  spine1: 'pelvis',
  spine0: 'pelvis',
  pelvis: 'pelvis',
  left_collar: 'left_wrist',
  left_shoulder: 'left_wrist',
  left_elbow: 'left_wrist',
  left_wrist: 'left_wrist',
  right_collar: 'right_wrist',
  right_shoulder: 'right_wrist',
  right_elbow: 'right_wrist',
  right_wrist: 'right_wrist',
  left_hip: 'left_ankle',
  left_knee: 'left_ankle',
  left_ankle: 'left_ankle',
  left_foot: 'left_ankle',
  right_hip: 'right_ankle',
  right_knee: 'right_ankle',
  right_ankle: 'right_ankle',
  right_foot: 'right_ankle',
};

export function shouldLockEffectorDuringPelvisIkMove(
  effectorId: KeyPoseIkEffectorId,
): boolean {
  return !KEY_POSE_IK_PELVIS_MOVE_BODY_FOLLOW_EFFECTOR_ID_SET.has(effectorId);
}

export function resolveKeyPoseIkEffectorFromFkJoint(
  jointId: KeyPoseFkJointId | null | undefined,
) {
  return jointId ? KEY_POSE_IK_EFFECTOR_BY_FK_JOINT_ID[jointId] : null;
}

export function getKeyPoseFkJointIdsForIkEffector(
  effectorId: KeyPoseIkEffectorId | null | undefined,
) {
  return effectorId ? KEY_POSE_FK_JOINT_IDS_BY_IK_EFFECTOR_ID[effectorId] : [];
}

export function resolveKeyPoseFkJointFromIkEffector(
  effectorId: KeyPoseIkEffectorId | null | undefined,
  preferredJointId?: KeyPoseFkJointId | null,
) {
  if (!effectorId) {
    return null;
  }

  const jointIds = getKeyPoseFkJointIdsForIkEffector(effectorId);
  if (preferredJointId && jointIds.includes(preferredJointId)) {
    return preferredJointId;
  }

  return effectorId;
}

export const KEY_POSE_IK_EFFECTOR_CONFIG_BY_ID: Record<
  KeyPoseIkEffectorId,
  KeyPoseIkEffectorConfig
> = {
  head: {
    id: 'head',
    boneNames: ['head', 'Head', 'c_head', 'mixamorigHead', 'mixamorig:Head'],
    color: '#faad14',
    label: 'Head',
  },
  pelvis: {
    id: 'pelvis',
    boneNames: [
      'pelvis',
      'Pelvis',
      'hips',
      'Hips',
      'root',
      'c_hips',
      'mixamorigHips',
      'mixamorig:Hips',
    ],
    color: '#ff4d4f',
    label: 'Pelvis',
  },
  left_wrist: {
    id: 'left_wrist',
    boneNames: [
      'left_wrist',
      'left_hand',
      'LeftWrist',
      'LeftHand',
      'l_wrist',
      'l_hand',
      'wrist_l',
      'hand_l',
      'c_l_hand',
      'mixamorigLeftHand',
      'mixamorig:LeftHand',
    ],
    color: '#2f54eb',
    label: 'Left wrist',
  },
  right_wrist: {
    id: 'right_wrist',
    boneNames: [
      'right_wrist',
      'right_hand',
      'RightWrist',
      'RightHand',
      'r_wrist',
      'r_hand',
      'wrist_r',
      'hand_r',
      'c_r_hand',
      'mixamorigRightHand',
      'mixamorig:RightHand',
    ],
    color: '#eb2f96',
    label: 'Right wrist',
  },
  left_ankle: {
    id: 'left_ankle',
    boneNames: [
      'left_ankle',
      'LeftAnkle',
      'l_talocrural',
      'l_ankle',
      'ankle_l',
      'mixamorigLeftFoot',
      'mixamorig:LeftFoot',
    ],
    color: '#13c2c2',
    label: 'Left ankle',
  },
  right_ankle: {
    id: 'right_ankle',
    boneNames: [
      'right_ankle',
      'RightAnkle',
      'r_talocrural',
      'r_ankle',
      'ankle_r',
      'mixamorigRightFoot',
      'mixamorig:RightFoot',
    ],
    color: '#52c41a',
    label: 'Right ankle',
  },
};

const KEY_POSE_IK_EFFECTOR_ID_SET = new Set<KeyPoseIkEffectorId>(
  KEY_POSE_IK_EFFECTOR_IDS,
);

const STRUCTURAL_FALLBACK_BY_EFFECTOR_ID: Partial<
  Record<
    KeyPoseIkEffectorId,
    {
      sourceEffectorId: KeyPoseIkEffectorId;
      boneParentSteps: number;
    }
  >
> = {
  pelvis: { sourceEffectorId: 'left_ankle', boneParentSteps: 3 },
};

export function isKeyPoseIkEffectorId(
  value: string,
): value is KeyPoseIkEffectorId {
  return KEY_POSE_IK_EFFECTOR_ID_SET.has(value as KeyPoseIkEffectorId);
}

export function resolveKeyPoseIkEffectorBoneName(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
) {
  const object = resolveKeyPoseIkEffectorObject(scene, effectorId);

  return isBone(object) ? object.name : null;
}

export function resolveKeyPoseIkEffectorObject(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
) {
  if (effectorId === 'pelvis') {
    return (
      resolveDirectKeyPoseIkEffectorObject(scene, effectorId) ??
      resolveStructuralKeyPoseIkEffectorObject(scene, effectorId)
    );
  }

  return (
    resolveDirectKeyPoseIkEffectorObject(scene, effectorId) ??
    resolveStructuralKeyPoseIkEffectorObject(scene, effectorId)
  );
}

function resolveDirectKeyPoseIkEffectorObject(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
) {
  const config = KEY_POSE_IK_EFFECTOR_CONFIG_BY_ID[effectorId];

  for (const boneName of config.boneNames) {
    const namedBone = scene.getObjectByName(boneName);
    if (isBone(namedBone)) {
      return namedBone;
    }
  }

  for (const boneName of config.boneNames) {
    const namedObject = scene.getObjectByName(boneName);
    if (namedObject) {
      return namedObject;
    }
  }

  let matchedObject: Object3D | null = null;
  scene.traverse((object) => {
    if (matchedObject || !isBone(object)) {
      return;
    }

    const autoAssign = getUserDataString(object, 'autoAssign');
    const originalName = getUserDataString(object, 'originalName');
    if (
      (autoAssign && config.boneNames.includes(autoAssign)) ||
      (originalName && config.boneNames.includes(originalName))
    ) {
      matchedObject = object;
    }
  });

  if (matchedObject) {
    return matchedObject;
  }

  scene.traverse((object) => {
    if (matchedObject) {
      return;
    }

    const autoAssign = getUserDataString(object, 'autoAssign');
    const originalName = getUserDataString(object, 'originalName');
    if (
      (autoAssign && config.boneNames.includes(autoAssign)) ||
      (originalName && config.boneNames.includes(originalName))
    ) {
      matchedObject = object;
    }
  });

  return matchedObject;
}

function resolveStructuralKeyPoseIkEffectorObject(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
) {
  const fallback = STRUCTURAL_FALLBACK_BY_EFFECTOR_ID[effectorId];
  if (!fallback) {
    return null;
  }

  const sourceObject = resolveDirectKeyPoseIkEffectorObject(
    scene,
    fallback.sourceEffectorId,
  );
  if (!sourceObject) {
    return null;
  }

  let current: Object3D | null = sourceObject;
  for (let index = 0; index < fallback.boneParentSteps; index += 1) {
    current = getClosestBoneParent(current);
    if (!current) {
      return null;
    }
  }

  return current;
}

function isBone(object: Object3D | null | undefined): object is Object3D {
  return Boolean(object && (object as Object3D & { isBone?: boolean }).isBone);
}

function getClosestBoneParent(object: Object3D): Object3D | null {
  let current: Object3D | null = object.parent;
  while (current) {
    if ((current as Object3D & { isBone?: boolean }).isBone === true) {
      return current;
    }

    current = current.parent;
  }

  return null;
}

function getUserDataString(object: Object3D, key: string) {
  const value = object.userData[key];

  return typeof value === 'string' ? value : null;
}
